/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * What work costs, and how much of it one person can hold.
 *
 * Two halves that only mean something together. A work-item value says a call
 * costs five and a chat costs one; a capacity profile says an agent can hold
 * ten. Neither number matters on its own, and the scale is what carries the
 * meaning rather than the absolute figures.
 *
 * Nothing is enforced until a profile is made the default. That is deliberate
 * and it is the one action on this page that can change who gets a call, so it
 * is spelled out rather than being a checkbox among others.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, ArrowBack, Delete, Star, StarBorder } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_BORDER = "1px solid #E6E8F0";

const userLabel = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.username || `User ${u.id}`;

function NewProfileDialog({ open, onClose, onCreate, saving }) {
  const [name, setName] = useState("");
  const [maxCapacity, setMaxCapacity] = useState(10);
  const [voice, setVoice] = useState(1);
  const [digital, setDigital] = useState(3);

  useEffect(() => {
    if (open) {
      setName("");
      setMaxCapacity(10);
      setVoice(1);
      setDigital(3);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>New capacity profile</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          label="Name"
          placeholder="Part-time agent"
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          autoFocus
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Total capacity"
          value={maxCapacity}
          onChange={(e) => setMaxCapacity(e.target.value)}
          margin="normal"
          helperText="In work-item units. Ten is one comfortable full-time load."
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Simultaneous calls"
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          margin="normal"
          helperText="Separate from total capacity: a call is exclusive."
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Simultaneous chats"
          value={digital}
          onChange={(e) => setDigital(e.target.value)}
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="dark" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!name.trim() || saving}
          onClick={() =>
            onCreate({
              name: name.trim(),
              max_capacity: Number(maxCapacity),
              max_voice_concurrency: Number(voice),
              max_digital_concurrency: Number(digital),
            })
          }
        >
          Create
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function AssignDialog({ open, onClose, onAssign, profiles, users, teams, saving }) {
  const [profileId, setProfileId] = useState("");
  const [kind, setKind] = useState("user");
  const [targetId, setTargetId] = useState("");

  useEffect(() => {
    if (open) {
      setProfileId("");
      setKind("user");
      setTargetId("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Assign a profile</DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text" mb={1}>
          The most specific assignment wins: a person&apos;s own beats their team&apos;s, which
          beats their role&apos;s, which beats the default.
        </MDTypography>
        <TextField
          select
          fullWidth
          size="small"
          label="Profile"
          value={profileId}
          onChange={(e) => setProfileId(e.target.value)}
          margin="normal"
        >
          {profiles.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name} ({p.max_capacity})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Applies to"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            setTargetId("");
          }}
          margin="normal"
        >
          <MenuItem value="user">A person</MenuItem>
          <MenuItem value="team">A team</MenuItem>
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label={kind === "user" ? "Person" : "Team"}
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          margin="normal"
        >
          {(kind === "user" ? users : teams).map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {kind === "user" ? userLabel(t) : t.name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="dark" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!profileId || !targetId || saving}
          onClick={() =>
            onAssign({
              profile_id: Number(profileId),
              ...(kind === "user"
                ? { user_id: Number(targetId) }
                : { team_id: Number(targetId) }),
            })
          }
        >
          Assign
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function QueueCapacityPage() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [types, setTypes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, p, a] = await Promise.all([
        axiosInstance.get("/call-center/settings/work-item-types"),
        axiosInstance.get("/call-center/settings/capacity-profiles"),
        axiosInstance.get("/call-center/settings/capacity-assignments"),
      ]);
      setTypes(Array.isArray(t.data) ? t.data : []);
      setProfiles(Array.isArray(p.data) ? p.data : []);
      setAssignments(Array.isArray(a.data) ? a.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load capacity settings."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Pickers only; a failure must not hide the settings.
    Promise.allSettled([axiosInstance.get("/users/"), axiosInstance.get("/teams")]).then(
      ([u, t]) => {
        setUsers(
          u.status === "fulfilled" && Array.isArray(u.value.data)
            ? u.value.data.filter((x) => x.is_active && !x.is_support_user)
            : []
        );
        setTeams(
          t.status === "fulfilled" && Array.isArray(t.value.data) ? t.value.data : []
        );
      }
    );
  }, [axiosInstance]);

  const saveWeight = async (type, weight) => {
    setError("");
    try {
      await axiosInstance.patch(`/call-center/settings/work-item-types/${type.id}`, {
        default_weight: Number(weight),
      });
      setToast("Saved. Work already in progress keeps the weight it started with.");
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save that value."));
      await load();
    }
  };

  const makeDefault = async (profile) => {
    setError("");
    try {
      await axiosInstance.patch(`/call-center/settings/capacity-profiles/${profile.id}`, {
        is_default: true,
      });
      setToast(`"${profile.name}" is now the default. Capacity limits are in force.`);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not make that the default."));
    }
  };

  const createProfile = async (payload) => {
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post("/call-center/settings/capacity-profiles", payload);
      setNewOpen(false);
      setToast("Profile created.");
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not create that profile."));
    } finally {
      setSaving(false);
    }
  };

  const assign = async (payload) => {
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post("/call-center/settings/capacity-assignments", payload);
      setAssignOpen(false);
      setToast("Assigned.");
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not assign that profile."));
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (row) => {
    setError("");
    try {
      await axiosInstance.delete(`/call-center/settings/capacity-assignments/${row.id}`);
      setToast("Removed.");
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not remove that assignment."));
    }
  };

  const enforcementOn = profiles.some((p) => p.is_default);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={isV2 ? 0 : 3}>
        {isV2 ? (
          <PageHeader
            title="Capacity & work-item values"
            summary="What each kind of work costs an agent, and how much any one person can hold."
          />
        ) : (
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={() => navigate("/settings/queues")}>
              <ArrowBack />
            </IconButton>
            <MDBox flexGrow={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Capacity &amp; work-item values
              </MDTypography>
              <MDTypography variant="body2" color="text">
                What each kind of work costs an agent, and how much any one person can hold.
              </MDTypography>
            </MDBox>
          </MDBox>
        )}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {!enforcementOn ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No profile is the default, so no capacity limit is being enforced and agents can be
            given unlimited work. Make one the default below to switch it on.
          </Alert>
        ) : null}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={26} />
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            {/* Work-item values */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
                <MDTypography variant="h6" fontWeight="medium">
                  What work costs
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={1}>
                  Only the ratios matter. A call at 5 against a capacity of 10 means one call is
                  half an agent.
                </MDTypography>
                <Divider sx={{ my: 1 }} />
                {types.length === 0 ? (
                  <MDTypography variant="body2" color="text">
                    Nothing configured, so every item counts as 1.
                  </MDTypography>
                ) : (
                  types.map((t) => (
                    <MDBox
                      key={t.id}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ borderBottom: "1px solid #F2F4F8", py: 1 }}
                    >
                      <MDBox flexGrow={1}>
                        <MDTypography variant="button" fontWeight="medium">
                          {t.label}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {t.object_api_name}
                          {t.channel !== "any" ? ` · ${t.channel}` : ""}
                        </MDTypography>
                      </MDBox>
                      <TextField
                        size="small"
                        type="number"
                        defaultValue={t.default_weight}
                        onBlur={(e) =>
                          Number(e.target.value) !== t.default_weight &&
                          saveWeight(t, e.target.value)
                        }
                        sx={{ width: 90 }}
                      />
                    </MDBox>
                  ))
                )}
              </Card>
            </Grid>

            {/* Profiles */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDBox flexGrow={1}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Capacity profiles
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setNewOpen(true)}
                  >
                    New
                  </MDButton>
                </MDBox>
                <Divider sx={{ my: 1 }} />
                {profiles.map((p) => (
                  <MDBox
                    key={p.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ borderBottom: "1px solid #F2F4F8", py: 1 }}
                  >
                    <Tooltip
                      title={
                        p.is_default
                          ? "The default: applies to anyone without a more specific assignment."
                          : "Make this the default"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={p.is_default}
                          onClick={() => makeDefault(p)}
                        >
                          {p.is_default ? (
                            <Star fontSize="small" color="warning" />
                          ) : (
                            <StarBorder fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <MDBox flexGrow={1}>
                      <MDTypography variant="button" fontWeight="medium">
                        {p.name}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {p.max_capacity} units · {p.max_voice_concurrency} call
                        {p.max_voice_concurrency === 1 ? "" : "s"} ·{" "}
                        {p.max_digital_concurrency} chats
                      </MDTypography>
                    </MDBox>
                    <Chip size="small" variant="outlined" label={`${p.assigned_agents} agents`} />
                  </MDBox>
                ))}
              </Card>
            </Grid>

            {/* Assignments */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDBox flexGrow={1}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Who gets which profile
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Anyone not listed here gets the default.
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<Add />}
                    disabled={profiles.length === 0}
                    onClick={() => setAssignOpen(true)}
                  >
                    Assign
                  </MDButton>
                </MDBox>
                <Divider sx={{ my: 1 }} />
                {assignments.length === 0 ? (
                  <MDTypography variant="body2" color="text">
                    Nothing assigned specifically — everyone is on the default.
                  </MDTypography>
                ) : (
                  assignments.map((a) => {
                    const profile = profiles.find((p) => p.id === a.profile_id);
                    return (
                      <MDBox
                        key={a.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ borderBottom: "1px solid #F2F4F8", py: 1 }}
                      >
                        <MDBox flexGrow={1}>
                          <MDTypography variant="button" fontWeight="medium">
                            {a.target_name || `${a.target_type} ${a.user_id || a.team_id}`}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {a.target_type} → {profile ? profile.name : "(deleted profile)"}
                          </MDTypography>
                        </MDBox>
                        <IconButton size="small" onClick={() => removeAssignment(a)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </MDBox>
                    );
                  })
                )}
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>

      <NewProfileDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreate={createProfile}
        saving={saving}
      />
      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onAssign={assign}
        profiles={profiles}
        users={users}
        teams={teams}
        saving={saving}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DashboardLayout>
  );
}

export default QueueCapacityPage;
